import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Avatar } from "@/components/ui/Avatar";
import { FlowMark } from "@/components/ui/Logo";
import { Icon, type IconName } from "@/components/ui/Icon";
import { WorkspaceBadge } from "@/components/ui/WorkspaceBadge";
import { CommandPalette } from "@/components/ui/CommandPalette";
import { NotificationsDropdown } from "@/components/layout/NotificationsDropdown";
import { BRAND } from "@/lib/tokens";
import { useTheme } from "@/hooks/useTheme";
import { useAuth } from "@/hooks/useAuth";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import { avatarUserFromFlowUser, deriveInitials, resolveWorkspaceColor } from "@/lib/colors";
import { t } from "@/lib/strings";

interface TopbarProps {
  /** Sidebar visibility, owned by AppShell (the only component rendering both). */
  railHidden?: boolean;
  onToggleRail?: () => void;
}

export function Topbar({ railHidden = false, onToggleRail }: TopbarProps = {}) {
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Pull workspaceId from the URL if the active route exposes it (works for
  // /w/$workspaceId; on /b/$boardId the wire-up will land when board pages
  // surface workspace context — for now we just fall back to "Wechseln").
  const params = useParams({ strict: false }) as { workspaceId?: string };
  const activeWorkspaceId = params.workspaceId ?? null;

  const wsQuery = useQuery({
    queryKey: queryKeys.workspaces,
    queryFn: api.listWorkspaces,
  });
  const workspaces = wsQuery.data ?? [];
  const activeWorkspace = activeWorkspaceId
    ? (workspaces.find((w) => w.id === activeWorkspaceId) ?? null)
    : null;
  const workspaceLabel = activeWorkspace?.name ?? t.workspaceSwitcherTitle;
  const workspaceChipColor = activeWorkspace
    ? resolveWorkspaceColor(activeWorkspace)
    : BRAND.orange;
  // Only users in 2+ workspaces get the interactive switcher. With exactly one,
  // there's nothing to switch to — show that workspace as a static label (using
  // workspaces[0] so it stays visible on Home, where activeWorkspace is null).
  const isMultiWorkspace = workspaces.length > 1;
  const soleWorkspace = workspaces.length === 1 ? workspaces[0] : null;

  const [menuOpen, setMenuOpen] = useState(false);
  const [wsMenuOpen, setWsMenuOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const wsMenuRef = useRef<HTMLDivElement | null>(null);

  // Global Ctrl+K shortcut for the command palette. No Apple users so we don't
  // also listen for Meta — keeps Cmd+K free in browsers that ship it for
  // address-bar search.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!wsMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (wsMenuRef.current && !wsMenuRef.current.contains(e.target as Node)) setWsMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setWsMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [wsMenuOpen]);

  const avatarUser = useMemo(
    () =>
      avatarUserFromFlowUser(user) ?? {
        id: "anon",
        display_name: "?",
        initials: deriveInitials(null),
        avatar_color: "slate" as const,
      },
    [user],
  );

  return (
    <header
      style={{
        height: 52,
        flexShrink: 0,
        background: "var(--brand-blue-bar)",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        padding: "0 14px 0 18px",
        gap: 14,
        borderBottom: "1px solid rgba(0,0,0,0.08)",
      }}
    >
      {onToggleRail ? (
        <button
          type="button"
          onClick={onToggleRail}
          title={railHidden ? t.railShow : t.railHide}
          aria-label={railHidden ? t.railShow : t.railHide}
          aria-expanded={!railHidden}
          onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.14)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 30,
            height: 30,
            marginLeft: -6,
            borderRadius: 7,
            border: "none",
            background: "transparent",
            // On the blue bar the icon is white like every other Topbar glyph;
            // dimmed when the rail is already showing so it reads as secondary.
            color: "#fff",
            opacity: railHidden ? 1 : 0.75,
            cursor: "pointer",
            transition: "background .12s, opacity .12s",
          }}
        >
          <Icon name="panelLeft" size={17} />
        </button>
      ) : null}
      <Link
        to="/"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          cursor: "pointer",
          textDecoration: "none",
          color: "#fff",
        }}
      >
        <FlowMark size={26} />
        <div style={{ display: "flex", flexDirection: "column", lineHeight: 1, gap: 2 }}>
          <span style={{ fontSize: 14, fontWeight: 600, letterSpacing: "0.01em" }}>Flow</span>
          <span
            style={{ fontSize: 10.5, color: "rgba(255,255,255,0.65)", letterSpacing: "0.04em" }}
          >
            by LogicLine
          </span>
        </div>
      </Link>

      <div
        style={{ width: 1, height: 22, background: "rgba(255,255,255,0.18)", margin: "0 4px" }}
      ></div>

      <div ref={wsMenuRef} style={{ position: "relative" }}>
        {isMultiWorkspace ? (
          <>
            <button
              type="button"
              onClick={() => setWsMenuOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={wsMenuOpen}
              aria-label={t.workspaceSwitcherTitle}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                height: 32,
                padding: "0 10px 0 8px",
                borderRadius: 8,
                border: "none",
                background: "rgba(255,255,255,0.08)",
                color: "#fff",
                cursor: "pointer",
                fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              <WorkspaceBadge
                name={activeWorkspace?.name ?? "W"}
                icon={activeWorkspace?.icon ?? null}
                color={workspaceChipColor}
                size={18}
              />
              {workspaceLabel}
              <Icon name="chevDown" size={13} color="rgba(255,255,255,0.7)" />
            </button>
            {wsMenuOpen ? (
              <div
                role="menu"
                style={{
                  position: "absolute",
                  left: 0,
                  top: "calc(100% + 6px)",
                  minWidth: 240,
                  maxHeight: 360,
                  overflow: "auto",
                  background: "var(--surface-elev)",
                  color: "var(--ink-1)",
                  borderRadius: 10,
                  border: "1px solid var(--hairline-strong)",
                  boxShadow: "0 10px 28px rgba(13,62,99,0.18), 0 2px 6px rgba(13,62,99,0.06)",
                  padding: 6,
                  zIndex: 60,
                }}
              >
                {workspaces.length === 0 ? (
                  <div style={{ padding: "10px 12px", fontSize: 13, color: "var(--ink-3)" }}>
                    {t.workspaceSwitcherEmpty}
                  </div>
                ) : (
                  workspaces.map((ws) => {
                    const isActive = ws.id === activeWorkspaceId;
                    return (
                      <button
                        key={ws.id}
                        role="menuitem"
                        onClick={() => {
                          setWsMenuOpen(false);
                          void navigate({ to: "/w/$workspaceId", params: { workspaceId: ws.id } });
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = "var(--surface-soft)")
                        }
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          width: "100%",
                          padding: "8px 10px",
                          background: "transparent",
                          border: "none",
                          borderRadius: 6,
                          cursor: "pointer",
                          fontSize: 13,
                          color: "var(--ink-1)",
                          fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
                          textAlign: "left",
                          fontWeight: isActive ? 600 : 400,
                        }}
                      >
                        <WorkspaceBadge
                          name={ws.name}
                          icon={ws.icon}
                          color={resolveWorkspaceColor(ws)}
                          size={18}
                        />
                        <span
                          style={{
                            flex: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {ws.name}
                        </span>
                        {isActive ? <Icon name="check" size={13} color="var(--ink-3)" /> : null}
                      </button>
                    );
                  })
                )}
              </div>
            ) : null}
          </>
        ) : soleWorkspace ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              height: 32,
              padding: "0 10px 0 8px",
              color: "#fff",
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            <WorkspaceBadge
              name={soleWorkspace.name}
              icon={soleWorkspace.icon}
              color={resolveWorkspaceColor(soleWorkspace)}
              size={18}
            />
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 200,
              }}
            >
              {soleWorkspace.name}
            </span>
          </div>
        ) : null}
      </div>

      <div style={{ flex: 1 }}></div>

      <button
        type="button"
        onClick={() => setPaletteOpen(true)}
        aria-label={t.paletteTitle}
        title={t.paletteTitle}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          height: 32,
          padding: "0 10px",
          minWidth: 240,
          borderRadius: 8,
          border: "1px solid rgba(255,255,255,0.14)",
          background: "rgba(255,255,255,0.06)",
          color: "rgba(255,255,255,0.85)",
          cursor: "pointer",
          fontSize: 12.5,
          fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
        }}
      >
        <Icon name="search" size={14} color="rgba(255,255,255,0.75)" />
        <span>{t.searchPlaceholder}</span>
        <span style={{ flex: 1 }}></span>
        <kbd
          style={{
            fontFamily: "JetBrains Mono Variable, JetBrains Mono, ui-monospace, monospace",
            fontSize: 10.5,
            padding: "2px 6px",
            background: "rgba(255,255,255,0.12)",
            borderRadius: 4,
            letterSpacing: "0.04em",
          }}
        >
          Strg+K
        </kbd>
      </button>

      <NotificationsDropdown />

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />

      <div ref={menuRef} style={{ position: "relative" }}>
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="User menu"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          style={{
            background: "transparent",
            border: "none",
            padding: 0,
            cursor: "pointer",
            borderRadius: 999,
          }}
        >
          <Avatar user={avatarUser} size={28} presence ringColor={BRAND.blue} />
        </button>
        {menuOpen ? (
          <div
            role="menu"
            style={{
              position: "absolute",
              right: 0,
              top: "calc(100% + 6px)",
              minWidth: 220,
              background: "var(--surface-elev)",
              color: "var(--ink-1)",
              borderRadius: 10,
              border: "1px solid var(--hairline-strong)",
              boxShadow: "0 10px 28px rgba(13,62,99,0.18), 0 2px 6px rgba(13,62,99,0.06)",
              padding: 6,
              fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
              zIndex: 60,
            }}
          >
            <div style={{ padding: "8px 10px 6px" }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{user?.name ?? "?"}</div>
              <div style={{ fontSize: 11.5, color: "var(--ink-3)" }}>{user?.email ?? ""}</div>
            </div>
            <div style={{ height: 1, background: "var(--hairline)", margin: "4px 0" }}></div>
            <MenuItem
              icon={theme === "dark" ? "sun" : "moon"}
              label={t.toggleTheme}
              onClick={() => {
                toggle();
                setMenuOpen(false);
              }}
            />
            <MenuItem
              icon="logout"
              label={t.signOut}
              onClick={() => {
                setMenuOpen(false);
                void logout();
              }}
            />
          </div>
        ) : null}
      </div>
    </header>
  );
}

function MenuItem({
  icon,
  label,
  onClick,
}: {
  icon: IconName;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      role="menuitem"
      onClick={onClick}
      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-soft)")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        width: "100%",
        padding: "8px 10px",
        background: "transparent",
        border: "none",
        borderRadius: 6,
        cursor: "pointer",
        fontSize: 13,
        color: "var(--ink-1)",
        fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
        textAlign: "left",
      }}
    >
      <Icon name={icon} size={15} color="var(--ink-3)" />
      {label}
    </button>
  );
}
